import { toastError } from '@onestep/coreui/Toast';
import axios from 'axios';
import { action, makeAutoObservable, reaction, runInAction } from 'mobx';

import type { typeOfCare } from '../../../components/OnBoardingWizard/OnBoardingWizardDrawer/StepperWizardContent/hooks/intake-options';
import { saveAs } from '../../../utils/blobUtils';
import type {
  IClinicalDocuments,
  IPatientAgent,
  IPatientRow,
  IPatientsStore,
} from '../../Patients/store';
import type { IAppStore } from '../../store';
import type { GenderEnum } from '../ClinicalRecord/clinicalRecordOptions';

export interface ICareTypeLog {
  careType: number;
  timestamp: string;
  uuid: string;
}

export interface IPayorLog {
  payor: number;
  timestamp: string;
  uuid: string;
}
export interface ITypeOfCare {
  careType?: typeOfCare;
  end?: string;
  patientUuid?: string;
  start?: string;
  uuid?: string;
  careTypeLog?: ICareTypeLog[];
  payorLog?: IPayorLog[];
  caseNumber?: string;
}

export enum activityName {
  tug = 'tug',
  sts = 'sts',
  fes = 'fes',
  fallConfidence = 'fall_confidence',
  kneeExtension = 'rom_knee_ext',
}

export interface IActivityMetadata {
  isAsc: boolean;
  maxAge: number;
  maxValue: number;
  minAge: number;
  minValue: number;
  version: number;
  activityName: activityName;
  gender: GenderEnum;
  breakpoints: { value: number; displayText: string }[];
}

interface IActivitiesThreshold {
  default: { [key in activityName]: IActivityMetadata };
  result: IActivityMetadata[];
}

export interface IPatientUserDetailsStore {
  activitiesThreshold: IActivitiesThreshold;
  data: { [key: string]: IPatientRow };
  patientsStore: IPatientsStore;
  agents: { [key: string]: IPatientAgent[] };
  getAgents(uuid: string): Promise<void>;

  isLoading: boolean;
  typeOfCareList: { [key: string]: ITypeOfCare[] };
  currentPatientUuid: string;

  uploadPatientFile(patientUuid: string, file: File, type?: string): Promise<IClinicalDocuments>;

  deletePatientFile(patientUuid: string, documentUuid: string): Promise<void>;

  getPatientFile(patientUuid: string, documentUuid: string, fileName: string): Promise<void>;

  getPatientTypeOfCareList(patientUuid: string): Promise<void>;
}

export default class PatientUserDetailsStore implements IPatientUserDetailsStore {
  data = {} as { [id: string]: IPatientRow };
  patientsStore: IPatientsStore;
  agents = {} as { [id: string]: IPatientAgent[] };
  isLoading = false;
  activitiesThreshold = {} as IActivitiesThreshold;
  typeOfCareList = {} as { [key: string]: ITypeOfCare[] };
  currentPatientUuid = '';

  constructor(patientsStore: IPatientsStore, appStore: IAppStore) {
    makeAutoObservable(this);
    this.patientsStore = patientsStore;
    reaction(
      () => {
        return appStore.clinicUuid;
      },
      () => {
        this.load();
      },
    );
  }

  @action
  async load() {
    this.isLoading = true;
    try {
      const result = await axios.get<IActivitiesThreshold>('/api/activity/threshold?version=1');
      runInAction(() => {
        this.activitiesThreshold = result.data;
      });
    } catch {
      toastError({ content: 'failed to load test thresholds' });
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async getAgents(uuid: string) {
    this.isLoading = true;

    try {
      const result = await axios.get(`/api/patients/${uuid}/agents`);
      runInAction(() => {
        this.agents[uuid] = result?.data?.result;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action
  async uploadPatientFile(patientUuid: string, file: File, type?: string) {
    const formData = new FormData();
    formData.append('clinical_document', file);
    if (type) formData.append('doc_type', type);
    const result = await axios.post(`/api/patients/${patientUuid}/clinical_document`, formData);
    runInAction(() => {
      if (!this.data[patientUuid].clinicalDocuments) {
        this.data[patientUuid].clinicalDocuments = [];
      }
      this.data[patientUuid].clinicalDocuments.push(result.data);
    });
    return result?.data;
  }

  @action
  async deletePatientFile(patientUuid: string, documentUuid: string) {
    await axios.delete(`/api/patients/${patientUuid}/clinical_document/${documentUuid}`);

    runInAction(() => {
      this.data[patientUuid].clinicalDocuments = this.data[patientUuid].clinicalDocuments.filter(
        (x) => x.uuid !== documentUuid,
      );
    });
  }

  @action
  async getPatientFile(patientUuid: string, documentUuid: string, fileName: string) {
    const result = await axios.get(
      `/api/patients/${patientUuid}/clinical_document/${documentUuid}`,
      {
        responseType: 'blob',
      },
    );
    runInAction(() => {
      const fileBlob = new Blob([result.data]);
      saveAs(fileBlob, fileName);
    });
  }

  @action
  async getPatientTypeOfCareList(patientUuid: string) {
    const result = await axios.get<{ result: ITypeOfCare[] }>(`/api/episode_of_care/list`, {
      params: {
        patientUuid,
      },
    });
    runInAction(() => {
      this.typeOfCareList[patientUuid] = result?.data?.result;
    });
  }
}
